import { useEffect, useState } from "react";
import { Menu, X, Globe, ExternalLink } from "lucide-react";
import { useLang } from "../context/LangContext";
import { scrollToId, waLink, OFFICIAL_URL } from "../constants";
import { Logo } from "./icons";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-switch-btn"
      className="flex items-center rounded-full border border-white/10 bg-white/5 p-0.5 text-xs font-semibold"
      role="group"
      aria-label="Language"
    >
      <Globe className="ml-2 h-3.5 w-3.5 text-slate-400" />
      {["cn", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1.5 transition-all duration-300 ${
            lang === l
              ? "bg-gradient-to-r from-blue-500 to-violet-500 text-white shadow-[0_0_14px_rgba(59,130,246,0.5)]"
              : "text-slate-400 hover:text-white"
          }`}
        >
          {l === "cn" ? "中文" : "EN"}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t, lang } = useLang();
  const official = lang === "cn" ? "官方网站" : "Official Website";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 80 : 0);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-2.5 shadow-[0_8px_40px_rgba(2,6,23,0.6)]" : "bg-transparent py-4"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo t={t.footer.tagline} />

        <nav className="hidden items-center gap-8 md:flex">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium text-slate-300 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-blue-400 to-violet-400 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <a
            href={OFFICIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-official-link"
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-blue-300"
          >
            {official}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch />
          <a
            href={waLink(t.contact.msgDefault)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_rgba(59,130,246,0.45)] transition-transform duration-300 hover:scale-[1.04] sm:flex"
          >
            {t.nav.cta}
          </a>
          <button
            className="rounded-lg border border-white/10 bg-white/5 p-2 text-white md:hidden"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="glass mx-4 mt-2 rounded-2xl p-4 md:hidden" data-testid="nav-mobile-menu">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-200 hover:bg-white/5"
            >
              {l.label}
            </button>
          ))}
          <a
            href={OFFICIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-official-link"
            className="mt-1 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-slate-400 hover:bg-white/5"
          >
            {official}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a
            href={waLink(t.contact.msgDefault)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 text-sm font-semibold text-white"
          >
            {t.nav.cta}
          </a>
        </div>
      )}
    </header>
  );
};
